import React from 'react';
import { BrandLogo } from './BrandLogo.tsx';
import { soundEngine } from '../utils/soundAndSpeech.ts';

interface PlatformFooterProps {
  onOpenAbout: () => void;
  onOpenSupport: () => void;
  onOpenFeedback: () => void;
  onNavigateLegal: (section: 'terms' | 'privacy' | 'guidelines') => void;
}

export const PlatformFooter: React.FC<PlatformFooterProps> = ({
  onOpenAbout,
  onOpenSupport,
  onOpenFeedback,
  onNavigateLegal,
}) => {
  return (
    <footer
      data-testid="platform-footer"
      className="mt-auto w-full border-t border-[var(--border-subtle)] bg-[var(--bg-surface)] pt-7 pb-[calc(5.25rem+env(safe-area-inset-bottom,14px))] md:py-8 px-4 sm:px-8 text-xs text-[var(--text-secondary)] transition-colors box-border"
    >
      <div className="w-full max-w-7xl mx-auto space-y-6">
        {/* Top Row: About, Support & Feedback Links — Centered on all viewports */}
        <div className="w-full flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1.5 text-xs font-medium text-[var(--text-secondary)] py-1 text-center">
          <button
            type="button"
            onClick={() => {
              soundEngine.playClick();
              onOpenAbout();
            }}
            className="hover:text-[var(--text-primary)] transition-colors cursor-pointer"
          >
            About the Creator
          </button>
          <span aria-hidden="true" className="text-[var(--text-muted)]">
            |
          </span>
          <button
            type="button"
            onClick={() => {
              soundEngine.playClick();
              onOpenSupport();
            }}
            className="hover:text-[var(--text-primary)] transition-colors cursor-pointer"
          >
            Support Quick!Bible
          </button>
          <span aria-hidden="true" className="text-[var(--text-muted)]">
            |
          </span>
          <button
            type="button"
            onClick={() => {
              soundEngine.playClick();
              onOpenFeedback();
            }}
            className="hover:text-[var(--text-primary)] transition-colors cursor-pointer"
          >
            Send Feedback
          </button>
        </div>

        {/* Bottom Row: Strictly Centered on Mobile/Tablet (< lg), Balanced Left/Right on Desktop (lg+) */}
        <div className="w-full pt-6 border-t border-[var(--border-subtle)] flex flex-col items-center justify-center gap-5 text-center lg:flex-row lg:items-center lg:justify-between lg:text-left lg:pt-5 lg:gap-6">
          {/* Brand Logo + Platform Description Block */}
          <div className="w-full max-w-md mx-auto flex flex-col items-center justify-center text-center lg:max-w-none lg:w-auto lg:mx-0 lg:flex-row lg:items-center lg:justify-start lg:text-left lg:gap-3.5">
            {/* Centered Logo Icon on Mobile */}
            <div className="mb-2.5 lg:mb-0 flex items-center justify-center mx-auto lg:mx-0 shrink-0">
              <BrandLogo
                size="xs"
                showWordmark={false}
                className="justify-center mx-auto"
              />
            </div>

            <div className="w-full space-y-1 flex flex-col items-center justify-center text-center lg:items-start lg:text-left">
              <div className="w-full flex flex-col items-center justify-center text-center lg:flex-row lg:flex-wrap lg:items-center lg:justify-start lg:text-left gap-x-2 gap-y-1">
                <div className="inline-flex items-center justify-center gap-1 font-bold text-[var(--text-primary)] text-sm lg:text-base tracking-tight mx-auto lg:mx-0">
                  <span>
                    Quick<span className="text-[#F97316]">!</span>Bible
                  </span>
                  <span className="text-[#F59E0B] text-[8px] sm:text-[9px] font-light font-serif tracking-[0.15em] uppercase translate-y-[0.5px]">
                    BETA
                  </span>
                </div>
                <span
                  data-testid="footer-platform-description"
                  className="text-[var(--text-muted)] font-normal text-xs sm:text-sm text-center lg:text-left leading-relaxed max-w-xs sm:max-w-md lg:max-w-none mx-auto lg:mx-0"
                >
                  Quick!Bible — Global Bible Quiz, Gaming &amp; Multiplayer Platform
                </span>
              </div>

              <div className="text-[11px] lg:text-xs text-[var(--text-muted)] text-center lg:text-left max-w-xs sm:max-w-md lg:max-w-none mx-auto lg:mx-0">
                A hobby project created by Adonis Melencion. Grounded in canonical public-domain Scripture.
              </div>
            </div>
          </div>

          {/* Legal Navigation Links */}
          <div className="w-full lg:w-auto flex flex-col items-center justify-center gap-2.5 lg:flex-row lg:gap-5 text-xs font-medium shrink-0">
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              <button
                type="button"
                onClick={() => onNavigateLegal('terms')}
                className="hover:text-[var(--text-primary)] transition-colors underline-offset-4 hover:underline cursor-pointer"
              >
                Terms of Use and EULA
              </button>
              <span aria-hidden="true" className="text-[var(--text-muted)]">
                |
              </span>
              <button
                type="button"
                onClick={() => onNavigateLegal('privacy')}
                className="hover:text-[var(--text-primary)] transition-colors underline-offset-4 hover:underline cursor-pointer"
              >
                Privacy Notice
              </button>
            </div>
            <button
              type="button"
              onClick={() => onNavigateLegal('guidelines')}
              className="hover:text-[var(--text-primary)] transition-colors underline-offset-4 hover:underline cursor-pointer"
            >
              Community Guidelines
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
